import type { ColumnDef } from '@tanstack/react-table'
import type { Invoice } from '@/api/resources/invoices'
import { useQuery } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import { Download } from 'lucide-react'
import { useState } from 'react'
import { toast } from 'sonner'
import { z } from 'zod/v4'
import { downloadInvoicePdf, invoicesQueryOptions } from '@/api/resources/invoices'
import { DataTable } from '@/components/data-table/data-table'
import { OrganisationFilter } from '@/components/layout/organisation-filter'
import { PageHeader } from '@/components/layout/page-header'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { useOrganisationFilter } from '@/hooks/use-organisation-filter'
import { usePagination } from '@/hooks/use-pagination'
import { formatCurrencyFromDecimal, formatDate } from '@/lib/format'

const searchSchema = z.object({
  page: z.coerce.number().int().positive().optional().default(1),
})

export const Route = createFileRoute('/_app/invoices/')({
  head: () => ({ meta: [{ title: 'Invoices - MyJunea' }] }),
  validateSearch: (search) => searchSchema.parse(search),
  component: InvoicesPage,
})

function DownloadButton({ invoice }: { invoice: Invoice }) {
  const [isDownloading, setIsDownloading] = useState(false)

  function handleDownload(): void {
    setIsDownloading(true)
    downloadInvoicePdf(invoice.organisation_id, invoice)
      .catch(() => {
        toast.error('Failed to download invoice PDF')
      })
      .finally(() => {
        setIsDownloading(false)
      })
  }

  return (
    <Button
      variant="ghost"
      size="icon"
      onClick={handleDownload}
      disabled={isDownloading}
      title="Download PDF"
    >
      <Download className="h-4 w-4" />
    </Button>
  )
}

function getStatusVariant(
  status: string,
  isPaid: boolean,
): 'default' | 'destructive' | 'outline' | 'secondary' {
  if (isPaid) return 'default'
  if (status.toLowerCase() === 'overdue') return 'destructive'
  return 'secondary'
}

const invoiceColumns: ColumnDef<Invoice, unknown>[] = [
  {
    accessorKey: 'invoice_code',
    header: 'Invoice',
  },
  {
    accessorKey: 'date',
    header: 'Date',
    cell: ({ row }) => {
      const date = row.original.date
      return date !== null ? formatDate(date) : '-'
    },
  },
  {
    accessorKey: 'total_amount',
    header: 'Total',
    cell: ({ row }) => formatCurrencyFromDecimal(row.original.total_amount),
  },
  {
    accessorKey: 'amount_paid',
    header: 'Paid',
    cell: ({ row }) => formatCurrencyFromDecimal(row.original.amount_paid),
  },
  {
    accessorKey: 'amount_outstanding',
    header: 'Outstanding',
    cell: ({ row }) => formatCurrencyFromDecimal(row.original.amount_outstanding),
  },
  {
    accessorKey: 'status',
    header: 'Status',
    cell: ({ row }) => (
      <Badge variant={getStatusVariant(row.original.status, row.original.is_paid)}>
        {row.original.status}
      </Badge>
    ),
  },
  {
    id: 'actions',
    header: '',
    cell: ({ row }) => <DownloadButton invoice={row.original} />,
  },
]

function InvoicesPage(): React.ReactElement {
  const { page } = Route.useSearch()
  const { setPage } = usePagination(page)
  const organisationFilter = useOrganisationFilter()

  const { data, isLoading } = useQuery(invoicesQueryOptions({ page, ...organisationFilter }))

  return (
    <div className="space-y-6">
      <PageHeader
        title="Invoices"
        description="View and download your invoices."
        filters={<OrganisationFilter />}
      />
      <DataTable
        columns={invoiceColumns}
        data={data?.data ?? []}
        isLoading={isLoading}
        pagination={
          data
            ? {
                current_page: data.meta.current_page,
                last_page: data.meta.last_page,
                total: data.meta.total,
              }
            : undefined
        }
        onPageChange={setPage}
        emptyMessage="No invoices found."
        renderMobileCard={(invoice) => (
          <div className="bg-card rounded-xl border p-4">
            <div className="flex items-start justify-between">
              <div>
                <div className="font-medium">{invoice.invoice_code}</div>
                <div className="text-muted-foreground text-sm">
                  {invoice.date !== null ? formatDate(invoice.date) : '-'}
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Badge variant={getStatusVariant(invoice.status, invoice.is_paid)}>
                  {invoice.status}
                </Badge>
                <DownloadButton invoice={invoice} />
              </div>
            </div>
            <div className="mt-3 grid grid-cols-3 gap-2 text-sm">
              <div>
                <div className="text-muted-foreground">Total</div>
                <div className="font-medium">{formatCurrencyFromDecimal(invoice.total_amount)}</div>
              </div>
              <div>
                <div className="text-muted-foreground">Paid</div>
                <div className="font-medium">{formatCurrencyFromDecimal(invoice.amount_paid)}</div>
              </div>
              <div>
                <div className="text-muted-foreground">Outstanding</div>
                <div className="font-medium">
                  {formatCurrencyFromDecimal(invoice.amount_outstanding)}
                </div>
              </div>
            </div>
          </div>
        )}
      />
    </div>
  )
}
